import React from 'react';
import * as yup from 'yup';
import {
    DModal,
    DSection,
    RhFormWrapper,
    RHTextField,
    RhCheckbox,
    RhSingleAutocomplete,
} from '../../../../dum';
import {
    GetPayrollEntriesQuery,
    useOrderProductionEmployeeInputQuery,
    useUpsertPayrollEntryMutation,
} from '../../../../../services';

interface IProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    entry: TEntry | null;
    payrollPeriodId: number;
    onSuccess: () => void;
}

type TEntry = GetPayrollEntriesQuery['getPayrollEntries'][number];

interface IForm {
    employee_id: number;
    estatus: string;
    control_bono_area: boolean;
    control_deposito: boolean;
    sueldo: number;
    jo: number;
    ht: number;
    he: number;
    he_override: boolean;
    retardos: number;
    faltas: number;
    vac: number;
    dias_festivos: number;
    infonavit: number;
    fonacot: number;
    descuento_prestamos: number;
    otros_menos: number;
    entregas_especiales: number;
    viajes: number;
    bonos: number;
    otros_mas: number;
    bd: number;
    observaciones: string;
}

const schema: yup.SchemaOf<IForm> = yup.object({
    employee_id: yup.number().required().min(1),
    estatus: yup.string().required(),
    control_bono_area: yup.boolean().required(),
    control_deposito: yup.boolean().required(),
    sueldo: yup.number().required().min(0),
    jo: yup.number().required().min(1),
    ht: yup.number().required().min(0),
    he: yup.number().required().min(0),
    he_override: yup.boolean().required(),
    retardos: yup.number().required().min(0),
    faltas: yup.number().required().min(0),
    vac: yup.number().required().min(0),
    dias_festivos: yup.number().required().min(0),
    infonavit: yup.number().required().min(0),
    fonacot: yup.number().required().min(0),
    descuento_prestamos: yup.number().required().min(0),
    otros_menos: yup.number().required().min(0),
    entregas_especiales: yup.number().required().min(0),
    viajes: yup.number().required().min(0),
    bonos: yup.number().required().min(0),
    otros_mas: yup.number().required().min(0),
    bd: yup.number().required().min(0),
    observaciones: yup.string().required(),
});

const emptyForm: IForm = {
    employee_id: 0,
    estatus: 'ALTA',
    control_bono_area: false,
    control_deposito: false,
    sueldo: 0,
    jo: 48,
    ht: 0,
    he: 0,
    he_override: false,
    retardos: 0,
    faltas: 0,
    vac: 0,
    dias_festivos: 0,
    infonavit: 0,
    fonacot: 0,
    descuento_prestamos: 0,
    otros_menos: 0,
    entregas_especiales: 0,
    viajes: 0,
    bonos: 0,
    otros_mas: 0,
    bd: 0,
    observaciones: '',
};

function entryToForm(e: TEntry): IForm {
    return {
        employee_id: e.employee_id,
        estatus: e.estatus,
        control_bono_area: e.control_bono_area,
        control_deposito: e.control_deposito,
        sueldo: e.sueldo,
        jo: e.jo,
        ht: e.ht,
        he: e.he,
        he_override: e.he_override,
        retardos: e.retardos,
        faltas: e.faltas,
        vac: e.vac,
        dias_festivos: e.dias_festivos,
        infonavit: e.infonavit,
        fonacot: e.fonacot,
        descuento_prestamos: e.descuento_prestamos,
        otros_menos: e.otros_menos,
        entregas_especiales: e.entregas_especiales,
        viajes: e.viajes,
        bonos: e.bonos,
        otros_mas: e.otros_mas,
        bd: e.bd,
        observaciones: e.observaciones,
    };
}

export function SUpsertPayrollEntryDialog({
    open,
    setOpen,
    entry,
    payrollPeriodId,
    onSuccess,
}: IProps) {
    const [upsert] = useUpsertPayrollEntryMutation();

    const { data: employeeData } = useOrderProductionEmployeeInputQuery({
        variables: { excludeDiscontinued: true },
        skip: !open,
    });

    const activeEmployees = (employeeData?.getEmployees ?? []).filter(
        (e) => !e.is_inactive,
    );

    const initialValues: IForm = entry ? entryToForm(entry) : emptyForm;

    const handleSubmit = async (values: IForm) => {
        await upsert({
            variables: {
                PayrollEntryInput: {
                    id: entry?.id,
                    payroll_period_id: payrollPeriodId,
                    ...values,
                },
            },
        });
        onSuccess();
        setOpen(false);
    };

    return (
        <DModal title="Entrada de nómina" open={open} setOpen={setOpen}>
            <RhFormWrapper
                initialValues={initialValues}
                validationSchema={schema}
                onSubmit={handleSubmit}
            >
                <DSection title="Empleado">
                    <RhSingleAutocomplete
                        name="employee_id"
                        label="Empleado"
                        options={activeEmployees}
                        itemValue="id"
                        itemText="fullname"
                    />
                    <RHTextField name="estatus" label="Estatus" />
                </DSection>
                <DSection title="Horas y salario">
                    <RHTextField name="sueldo" label="Sueldo" />
                    <RHTextField name="jo" label="JO (hrs estándar)" />
                    <RHTextField name="ht" label="HT (hrs trabajadas)" />
                    <RhCheckbox name="he_override" label="Editar HE manualmente" />
                    <RHTextField name="he" label="HE (hrs extra)" />
                    <RHTextField name="retardos" label="Retardos" />
                    <RHTextField name="faltas" label="Faltas" />
                    <RHTextField name="vac" label="Días vacaciones" />
                    <RHTextField name="dias_festivos" label="Días festivos" />
                </DSection>
                <DSection title="Bonos">
                    <RHTextField name="bd" label="BD (disponibilidad)" />
                    <RHTextField name="entregas_especiales" label="Entregas especiales" />
                    <RHTextField name="viajes" label="Viajes" />
                    <RHTextField name="bonos" label="Bonos" />
                    <RHTextField name="otros_mas" label="Otros +" />
                </DSection>
                <DSection title="Deducciones">
                    <RHTextField name="infonavit" label="INFONAVIT" />
                    <RHTextField name="fonacot" label="FONACOT" />
                    <RHTextField name="descuento_prestamos" label="Préstamos / adelantos" />
                    <RHTextField name="otros_menos" label="Otros -" />
                </DSection>
                <DSection title="Notas">
                    <RHTextField name="observaciones" label="Observaciones" />
                </DSection>
            </RhFormWrapper>
        </DModal>
    );
}
